import { useMemo } from "react";
import { destLabel } from "./lib/destinations";
import { LANGS } from "./lib/resolveLang";
import { STATIONS } from "./sim/stations";

export { LANGS };

export const LOCALES = {
  en: "en-GB",
  uk: "uk-UA",
  ru: "ru-RU",
  bg: "bg-BG",
};

export function localeFor(lang) {
  return LOCALES[lang] || LOCALES.en;
}

export const STR = {
  en: {
    appTitle: "Digital Station",
    appSub: "Dispatcher cockpit · marshalling yard",
    // Read by the tab, the bookmark, the history entry and every link preview,
    // so it lives in the catalog with everything else rather than in index.html.
    docDescription:
      "Digital Station — an interactive demo dispatcher cockpit for a marshalling yard: wagons, tracks, an OCR queue and an event journal.",
    workerAppSub: "Mobile inspector app",
    role: "Role",
    dispatcher: "Dispatcher",
    overview: "All stations",
    stations: "Stations",
    highlight: "Highlight mode",
    dwell: "Dwell",
    cargo: "Cargo",
    weight: "Weight",
    legend: "Legend",
    short: "under 15 min",
    medium: "15–45 min",
    long: "> 45 min",
    coal: "Coal",
    empty: "Empty",
    foreign: "Foreign",
    light: "Light",
    heavy: "Heavy",
    overweight: "Overload",
    totalWagons: "Wagons total",
    alerts: "Alerts",
    track: "Track",
    capacity: "Capacity",
    overflow: "OVERFLOW",
    wagon: "Wagon",
    details: "Card",
    journal: "Log",
    ocrQueue: "OCR queue",
    number: "Wagon number",
    bruttoW: "Gross weight, t",
    nettoW: "Net weight, t",
    destination: "Destination",
    owner: "Ownership",
    own: "Own",
    note: "Note",
    dwellTime: "Dwell time",
    status: "Status",
    ocrConf: "OCR confidence",
    photo: "Photo evidence",
    save: "Save",
    moveTo: "Move to track",
    moveBtn: "Move",
    selectWagon: "Select a wagon on the schematic",
    selectWagonHint: "Click any wagon to view or edit its data.",
    export: "Export consist (CSV)",
    reset: "Reset data",
    confirm: "Confirm",
    reject: "Reject",
    proposed: "Recognised",
    noOcr: "Nothing to review",
    updated: "Updated",
    by: "by",
    sorting: "Marshalling",
    loading: "Loading",
    minutes: "min",
    hours: "h",
    saved: "Changes saved",
    moved: "Wagon moved",
    resolved: "Number processed",
    liveMode: "LIVE · auto-simulation",
    wagonsOnTrack: "wg.",
    confidence: "Confidence",
    worker: "Worker",
    actorDispatcher: "dispatcher",
    actorWorker: "station worker",
    intake: "Intake",
    trainNo: "Consist number",
    intakeStation: "Station",
    intakeTrack: "Reception track",
    freeSlots: "free",
    wagonList: "Consist wagons",
    addRow: "Add wagon",
    acceptTrainBtn: "Accept consist",
    acceptedTrain: "Consist accepted",
    partialAccept: "Partially accepted — not enough room on the track",
    noSpaceTrack: "No room on the selected track",
    fillTrainNo: "Enter consist number and at least one wagon",
    hintTrainNoRequired: "Enter consist number",
    hintWagonRequired: "Add at least one wagon with a number (min. 4 digits)",
    legendMarkers: "Markers",
    markerOcrCheck: "number needs review (OCR)",
    markerForeign: "foreign wagon",
    markerBogieSkew: "uneven bogie loading",
    weighing: "Weighing",
    bogie1: "Bogie 1",
    bogie2: "Bogie 2",
    totalWeight: "Total weight",
    bogieImbalanceWarn: "Uneven bogie loading — risk of railway rejection",
    numberShort: "No.",
    bruttoShort: "Gross",
    nettoShort: "Net",
    destShort: "Destination",
    intakeSummary: "Consist totals",
    coupledError: "Wagon is coupled in the consist — only the end wagon can be shunted",
    trackFullError: "Track is full",
    cantMove: "Cannot move right now",
    statusParked: "On track",
    statusArriving: "Arriving",
    statusShunting: "Shunting",
    statusDeparting: "Departing",
    statusLoaded: "Loaded",
    tabPaths: "Tracks",
    tabOcr: "OCR",
    back: "Back",
    selectStation: "Station",
    noWagonsOnTrack: "No wagons on this track",
    editWagon: "Edit wagon",
    wagonDeparted: "Wagon has already left the station",
    dataReset: "Data reset",
    wagonAria: (num, cargo, track) => `Wagon ${num}, ${cargo}, track ${track}`,
    csvTrack: "Track",
    csvNumber: "Wagon number",
    csvCargo: "Cargo",
    csvOwner: "Ownership",
    csvBrutto: "Gross weight, t",
    csvNetto: "Net weight, t",
    csvDest: "Destination",
    csvDwell: "Dwell, min",
    csvOcr: "OCR confidence",
    csvForeign: "Foreign",
    weightRange3070: "30–70 t",
    weightRange7094: "70–94 t",
    sourceLocomotive: "locomotive",
    sourceScale: "weighbridge",
    sourceShunt: "shunting",
    sourceCamera: "camera",
    sourceSystem: "system",
    sourceDispatcher: "dispatcher",
    sourceWorker: "worker",
    ocrActionConfirmed: "confirmed",
    ocrActionRejected: "rejected",
    eventArrive: "Train arrived at {station} — {count} wg.",
    eventLoad: "{count} wg. loaded with coal at {station}.",
    eventTransfer: "Consist dispatched from {from} to {to} ({count} wg.).",
    eventDepart: "Consist formed, dispatched: {dest} ({count} wg.).",
    eventMoveShunt: "Wagon {number}: shunted to {track}.",
    eventMoveDispatcher: "Dispatcher moved wagon {number} to {track}.",
    eventEdit: "Wagon {number} data changed ({actor}).",
    eventOcrLow: "Camera: number recognised with low confidence — review required.",
    eventOcrResolved: "Number {number} {action} manually ({actor}).",
    eventIntake: "{actor} accepted consist {trainNo} at {station}, {track} ({count} wg.).",
    eventSystemStart: "Digital Station started. Simulation is running.",
    eventSystemReset: "Data reset and regenerated.",
    tonneUnit: "t",
    consistNote: "Consist {trainNo}",
    introLead:
      "This is an interactive demo of a rail-yard dispatcher system. Every facility name, wagon number and weight in it is fictional.",
    introBulletScreen:
      "The screen shows stations, tracks and wagons of a marshalling yard in a live simulation.",
    introBulletRoles:
      "Two roles — the dispatcher desk and the yard walker's mobile app — switch in the sidebar.",
    introBulletTime:
      "Dwell time runs accelerated: 1 real second equals 30 simulated, so a viewing session shows wagons ageing out of the green zone.",
    introClose: "Enter the demo",
    demoBadge: "Demo · fictional data",
    ctaSite: "Built by BrilliantFlux",
    ctaBooking: "Discuss a project",
    loadingYard: "LOADING YARD…",
  },
  uk: {
    appTitle: "Цифрова станція",
    appSub: "Диспетчерський кокпіт · сортувальний вузол",
    docDescription:
      "Цифрова станція — інтерактивний демонстраційний диспетчерський кокпіт сортувальної станції: вагони, колії, OCR-черга, журнал подій.",
    workerAppSub: "Мобільний додаток обхідника",
    role: "Роль",
    dispatcher: "Диспетчер",
    overview: "Огляд усіх станцій",
    stations: "Станції",
    highlight: "Режим підсвічування",
    dwell: "Простій",
    cargo: "Вантаж",
    weight: "Вага",
    legend: "Легенда",
    short: "до 15 хв",
    medium: "15–45 хв",
    long: "> 45 хв",
    coal: "Вугілля",
    empty: "Порожній",
    foreign: "Чужий",
    light: "Легкий",
    heavy: "Важкий",
    overweight: "Перевантаж",
    totalWagons: "Вагонів усього",
    alerts: "Тривоги",
    track: "Колія",
    capacity: "Місткість",
    overflow: "ПЕРЕПОВНЕННЯ",
    wagon: "Вагон",
    details: "Картка",
    journal: "Журнал",
    ocrQueue: "Черга OCR",
    number: "Номер вагона",
    bruttoW: "Вага брутто, т",
    nettoW: "Вага нетто, т",
    destination: "Станція призначення",
    owner: "Належність",
    own: "Свій",
    note: "Нотатка",
    dwellTime: "Простій",
    status: "Статус",
    ocrConf: "Впевненість OCR",
    photo: "Фото-доказ",
    save: "Зберегти",
    moveTo: "Перемістити на колію",
    moveBtn: "Перемістити",
    selectWagon: "Оберіть вагон на схемі",
    selectWagonHint: "Натисніть на будь-який вагон, щоб переглянути дані або внести зміни.",
    export: "Експорт поїзда (CSV)",
    reset: "Скинути дані",
    confirm: "Підтвердити",
    reject: "Відхилити",
    proposed: "Розпізнано",
    noOcr: "Немає елементів на перевірці",
    updated: "Оновлено",
    by: "ким",
    sorting: "Сортувальна",
    loading: "Навантажувальна",
    minutes: "хв",
    hours: "год",
    saved: "Зміни збережено",
    moved: "Вагон переміщено",
    resolved: "Номер оброблено",
    liveMode: "LIVE · авто-симуляція",
    wagonsOnTrack: "ваг.",
    confidence: "Впевненість",
    worker: "Працівник",
    actorDispatcher: "диспетчер",
    actorWorker: "працівник станції",
    intake: "Приймання",
    trainNo: "Номер поїзда",
    intakeStation: "Станція",
    intakeTrack: "Колія приймання",
    freeSlots: "вільно",
    wagonList: "Вагони поїзда",
    addRow: "Додати вагон",
    acceptTrainBtn: "Прийняти поїзд",
    acceptedTrain: "Поїзд прийнято",
    partialAccept: "Прийнято частково — забракло місця на колії",
    noSpaceTrack: "Немає місця на обраній колії",
    fillTrainNo: "Вкажіть номер поїзда та хоча б один вагон",
    hintTrainNoRequired: "Вкажіть номер поїзда",
    hintWagonRequired: "Додайте хоча б один вагон з номером (мінімум 4 цифри)",
    legendMarkers: "Позначки",
    markerOcrCheck: "номер потребує перевірки (OCR)",
    markerForeign: "чужий вагон",
    markerBogieSkew: "нерівномірне навантаження на візки",
    weighing: "Зважування",
    bogie1: "Візок 1",
    bogie2: "Візок 2",
    totalWeight: "Загальна вага",
    bogieImbalanceWarn: "Нерівномірне завантаження візків — ризик браку УЗ",
    numberShort: "№",
    bruttoShort: "Брутто",
    nettoShort: "Нетто",
    destShort: "Напрям",
    intakeSummary: "Разом по поїзду",
    coupledError: "Вагон затиснутий у зчепленні — можна переміщувати лише крайній вагон",
    trackFullError: "Колія заповнена",
    cantMove: "Переміщення зараз неможливе",
    statusParked: "На колії",
    statusArriving: "Прибуття",
    statusShunting: "Маневр",
    statusDeparting: "Відправлення",
    statusLoaded: "Завантажено",
    tabPaths: "Колії",
    tabOcr: "OCR",
    back: "Назад",
    selectStation: "Станція",
    noWagonsOnTrack: "На колії немає вагонів",
    editWagon: "Редагування вагона",
    wagonDeparted: "Вагон вже покинув станцію",
    dataReset: "Дані скинуто",
    wagonAria: (num, cargo, track) => `Вагон ${num}, ${cargo}, колія ${track}`,
    csvTrack: "Колія",
    csvNumber: "Номер вагона",
    csvCargo: "Вантаж",
    csvOwner: "Належність",
    csvBrutto: "Вага брутто, т",
    csvNetto: "Вага нетто, т",
    csvDest: "Станція призначення",
    csvDwell: "Простій, хв",
    csvOcr: "Впевненість OCR",
    csvForeign: "Чужий",
    weightRange3070: "30–70 т",
    weightRange7094: "70–94 т",
    sourceLocomotive: "тепловоз",
    sourceScale: "вагова",
    sourceShunt: "маневр",
    sourceCamera: "камера",
    sourceSystem: "система",
    sourceDispatcher: "диспетчер",
    sourceWorker: "працівник",
    ocrActionConfirmed: "підтверджено",
    ocrActionRejected: "відхилено",
    eventArrive: "На {station} прибув поїзд — {count} ваг.",
    eventLoad: "На {station} завантажено вугіллям {count} ваг.",
    eventTransfer: "Поїзд з {from} відправлено на {to} ({count} ваг.).",
    eventDepart: "Сформовано поїзд, відправлено: {dest} ({count} ваг.).",
    eventMoveShunt: "Вагон {number}: маневрове переміщення на {track}.",
    eventMoveDispatcher: "Диспетчер перемістив вагон {number} на {track}.",
    eventEdit: "Дані вагона {number} змінено ({actor}).",
    eventOcrLow: "Камера: номер розпізнано з низькою впевненістю — потрібна перевірка.",
    eventOcrResolved: "Номер {number} {action} вручну ({actor}).",
    eventIntake: "{actor} прийняв поїзд {trainNo} на {station}, {track} ({count} ваг.).",
    eventSystemStart: "Цифрова станція запущена. Симуляція активна.",
    eventSystemReset: "Дані скинуто та згенеровано заново.",
    tonneUnit: "т",
    consistNote: "Поїзд {trainNo}",
    introLead:
      "Це інтерактивне демо диспетчерської системи сортувальної станції. Усі назви об'єктів, номери вагонів і вага в ньому вигадані.",
    introBulletScreen:
      "На екрані — станції, колії та вагони сортувального вузла в живій симуляції.",
    introBulletRoles:
      "Дві ролі — стіл диспетчера та мобільний додаток обхідника — перемикаються в бічній панелі.",
    introBulletTime:
      "Простій іде прискорено: 1 реальна секунда = 30 симульованих, тож за сеанс вагони встигають вийти із зеленої зони.",
    introClose: "Перейти до демо",
    demoBadge: "Демо · вигадані дані",
    ctaSite: "Зроблено в BrilliantFlux",
    ctaBooking: "Обговорити проєкт",
    loadingYard: "ЗАВАНТАЖЕННЯ СТАНЦІЇ…",
  },
  ru: {
    appTitle: "Цифровая станция",
    appSub: "Диспетчерский кокпит · сортировочный узел",
    docDescription:
      "Цифровая станция — интерактивный демонстрационный диспетчерский кокпит сортировочной станции: вагоны, пути, OCR-очередь, журнал событий.",
    workerAppSub: "Мобильное приложение обходчика",
    role: "Роль",
    dispatcher: "Диспетчер",
    overview: "Обзор всех станций",
    stations: "Станции",
    highlight: "Режим подсветки",
    dwell: "Простой",
    cargo: "Груз",
    weight: "Вес",
    legend: "Легенда",
    short: "до 15 мин",
    medium: "15–45 мин",
    long: "> 45 мин",
    coal: "Уголь",
    empty: "Порожний",
    foreign: "Чужой",
    light: "Лёгкий",
    heavy: "Тяжёлый",
    overweight: "Перегруз",
    totalWagons: "Вагонов всего",
    alerts: "Тревоги",
    track: "Путь",
    capacity: "Вместимость",
    overflow: "ПЕРЕПОЛНЕНИЕ",
    wagon: "Вагон",
    details: "Карточка",
    journal: "Журнал",
    ocrQueue: "Очередь OCR",
    number: "Номер вагона",
    bruttoW: "Вес брутто, т",
    nettoW: "Вес нетто, т",
    destination: "Станция назначения",
    owner: "Принадлежность",
    own: "Свой",
    note: "Заметка",
    dwellTime: "Простой",
    status: "Статус",
    ocrConf: "Уверенность OCR",
    photo: "Фото-доказательство",
    save: "Сохранить",
    moveTo: "Переместить на путь",
    moveBtn: "Переместить",
    selectWagon: "Выберите вагон на схеме",
    selectWagonHint: "Нажмите на любой вагон, чтобы посмотреть данные или внести изменения.",
    export: "Экспорт состава (CSV)",
    reset: "Сбросить данные",
    confirm: "Подтвердить",
    reject: "Отклонить",
    proposed: "Распознано",
    noOcr: "Нет элементов на проверке",
    updated: "Обновлено",
    by: "кем",
    sorting: "Сортировочная",
    loading: "Погрузочная",
    minutes: "мин",
    hours: "ч",
    saved: "Изменения сохранены",
    moved: "Вагон перемещён",
    resolved: "Номер обработан",
    liveMode: "LIVE · авто-симуляция",
    wagonsOnTrack: "ваг.",
    confidence: "Уверенность",
    worker: "Рабочий",
    actorDispatcher: "диспетчер",
    actorWorker: "рабочий станции",
    intake: "Приёмка",
    trainNo: "Номер состава",
    intakeStation: "Станция",
    intakeTrack: "Путь приёма",
    freeSlots: "свободно",
    wagonList: "Вагоны состава",
    addRow: "Добавить вагон",
    acceptTrainBtn: "Принять состав",
    acceptedTrain: "Состав принят",
    partialAccept: "Принято частично — не хватило места на пути",
    noSpaceTrack: "Нет места на выбранном пути",
    fillTrainNo: "Укажите номер состава и хотя бы один вагон",
    hintTrainNoRequired: "Укажите номер состава",
    hintWagonRequired: "Добавьте хотя бы один вагон с номером (минимум 4 цифры)",
    legendMarkers: "Обозначения",
    markerOcrCheck: "номер требует проверки (OCR)",
    markerForeign: "чужой вагон",
    markerBogieSkew: "неравномерная нагрузка на тележки",
    weighing: "Взвешивание",
    bogie1: "Тележка 1",
    bogie2: "Тележка 2",
    totalWeight: "Общий вес",
    bogieImbalanceWarn: "Неравномерная загрузка тележек — риск брака УЗ",
    numberShort: "№",
    bruttoShort: "Брутто",
    nettoShort: "Нетто",
    destShort: "Направление",
    intakeSummary: "Итого по составу",
    coupledError: "Вагон зажат в сцепке — можно перемещать только крайний вагон",
    trackFullError: "Путь заполнен",
    cantMove: "Перемещение сейчас невозможно",
    statusParked: "На пути",
    statusArriving: "Прибытие",
    statusShunting: "Манёвр",
    statusDeparting: "Отправление",
    statusLoaded: "Загружен",
    tabPaths: "Пути",
    tabOcr: "OCR",
    back: "Назад",
    selectStation: "Станция",
    noWagonsOnTrack: "На пути нет вагонов",
    editWagon: "Редактирование вагона",
    wagonDeparted: "Вагон уже покинул станцию",
    dataReset: "Данные сброшены",
    wagonAria: (num, cargo, track) => `Вагон ${num}, ${cargo}, путь ${track}`,
    csvTrack: "Путь",
    csvNumber: "Номер вагона",
    csvCargo: "Груз",
    csvOwner: "Принадлежность",
    csvBrutto: "Вес брутто, т",
    csvNetto: "Вес нетто, т",
    csvDest: "Станция назначения",
    csvDwell: "Простой, мин",
    csvOcr: "Уверенность OCR",
    csvForeign: "Чужой",
    weightRange3070: "30–70 т",
    weightRange7094: "70–94 т",
    sourceLocomotive: "тепловоз",
    sourceScale: "весовая",
    sourceShunt: "манёвр",
    sourceCamera: "камера",
    sourceSystem: "система",
    sourceDispatcher: "диспетчер",
    sourceWorker: "рабочий",
    ocrActionConfirmed: "подтверждён",
    ocrActionRejected: "отклонён",
    eventArrive: "На {station} прибыл поезд — {count} ваг.",
    eventLoad: "На {station} загружено углём {count} ваг.",
    eventTransfer: "Состав с {from} отправлен на {to} ({count} ваг.).",
    eventDepart: "Сформирован состав, отправлен: {dest} ({count} ваг.).",
    eventMoveShunt: "Вагон {number}: маневровое перемещение на {track}.",
    eventMoveDispatcher: "Диспетчер переместил вагон {number} на {track}.",
    eventEdit: "Данные вагона {number} изменены ({actor}).",
    eventOcrLow: "Камера: номер распознан с низкой уверенностью — нужна проверка.",
    eventOcrResolved: "Номер {number} {action} вручную ({actor}).",
    eventIntake: "{actor} принял состав {trainNo} на {station}, {track} ({count} ваг.).",
    eventSystemStart: "Цифровая станция запущена. Симуляция активна.",
    eventSystemReset: "Данные сброшены и заново сгенерированы.",
    tonneUnit: "т",
    consistNote: "Состав {trainNo}",
    introLead:
      "Это интерактивное демо диспетчерской системы сортировочной станции. Все названия объектов, номера вагонов и вес в нём вымышлены.",
    introBulletScreen:
      "На экране — станции, пути и вагоны сортировочного узла в живой симуляции.",
    introBulletRoles:
      "Две роли — стол диспетчера и мобильное приложение обходчика — переключаются в боковой панели.",
    introBulletTime:
      "Простой идёт ускоренно: 1 реальная секунда = 30 симулированных, поэтому за сеанс вагоны успевают выйти из зелёной зоны.",
    introClose: "Перейти к демо",
    demoBadge: "Демо · вымышленные данные",
    ctaSite: "Сделано в BrilliantFlux",
    ctaBooking: "Обсудить проект",
    loadingYard: "ЗАГРУЗКА СТАНЦИИ…",
  },
  bg: {
    appTitle: "Цифрова гара",
    appSub: "Диспечерски кокпит · сортировъчна гара",
    docDescription:
      "Цифрова гара — интерактивен демонстрационен диспечерски кокпит на сортировъчна гара: вагони, коловози, OCR опашка, дневник на събитията.",
    workerAppSub: "Мобилно приложение на обходчика",
    role: "Роля",
    dispatcher: "Диспечер",
    overview: "Преглед на всички гари",
    stations: "Гари",
    highlight: "Режим на подсветка",
    dwell: "Престой",
    cargo: "Товар",
    weight: "Тегло",
    legend: "Легенда",
    short: "до 15 мин",
    medium: "15–45 мин",
    long: "> 45 мин",
    coal: "Въглища",
    empty: "Празен",
    foreign: "Чужд",
    light: "Лек",
    heavy: "Тежък",
    overweight: "Претоварване",
    totalWagons: "Вагони общо",
    alerts: "Тревоги",
    track: "Коловоз",
    capacity: "Капацитет",
    overflow: "ПРЕПЪЛВАНЕ",
    wagon: "Вагон",
    details: "Карта",
    journal: "Журнал",
    ocrQueue: "Опашка OCR",
    number: "Номер на вагона",
    bruttoW: "Бруто тегло, т",
    nettoW: "Нето тегло, т",
    destination: "Гара на назначение",
    owner: "Принадлежност",
    own: "Собствен",
    note: "Бележка",
    dwellTime: "Престой",
    status: "Статус",
    ocrConf: "Увереност OCR",
    photo: "Фотодоказателство",
    save: "Запис",
    moveTo: "Премести на коловоз",
    moveBtn: "Премести",
    selectWagon: "Изберете вагон на схемата",
    selectWagonHint: "Натиснете който и да е вагон, за да видите данните или да ги промените.",
    export: "Експорт на състава (CSV)",
    reset: "Нулиране на данните",
    confirm: "Потвърди",
    reject: "Отхвърли",
    proposed: "Разпознато",
    noOcr: "Няма елементи за проверка",
    updated: "Обновено",
    by: "от",
    sorting: "Сортировъчна",
    loading: "Товаро-разтоварна",
    minutes: "мин",
    hours: "ч",
    saved: "Промените са записани",
    moved: "Вагонът е преместен",
    resolved: "Номерът е обработен",
    liveMode: "LIVE · автосимулация",
    wagonsOnTrack: "ваг.",
    confidence: "Увереност",
    worker: "Работник",
    actorDispatcher: "диспечер",
    actorWorker: "работник на гарата",
    intake: "Приемане",
    trainNo: "Номер на състава",
    intakeStation: "Гара",
    intakeTrack: "Коловоз за приемане",
    freeSlots: "свободно",
    wagonList: "Вагони в състава",
    addRow: "Добави вагон",
    acceptTrainBtn: "Приеми състава",
    acceptedTrain: "Съставът е приет",
    partialAccept: "Прието частично — няма достатъчно място на коловоза",
    noSpaceTrack: "Няма място на избрания коловоз",
    fillTrainNo: "Посочете номер на състава и поне един вагон",
    hintTrainNoRequired: "Посочете номер на състава",
    hintWagonRequired: "Добавете поне един вагон с номер (минимум 4 цифри)",
    legendMarkers: "Означения",
    markerOcrCheck: "номерът изисква проверка (OCR)",
    markerForeign: "чужд вагон",
    markerBogieSkew: "неравномерно натоварване на талигите",
    weighing: "Претегляне",
    bogie1: "Талига 1",
    bogie2: "Талига 2",
    totalWeight: "Общо тегло",
    bogieImbalanceWarn: "Неравномерно натоварване на талигите — риск от брак по жп норма",
    numberShort: "№",
    bruttoShort: "Бруто",
    nettoShort: "Нето",
    destShort: "Направление",
    intakeSummary: "Общо за състава",
    coupledError: "Вагонът е в средата на сцепката — може да се маневрира само крайният вагон",
    trackFullError: "Коловозът е пълен",
    cantMove: "Преместването в момента е невъзможно",
    statusParked: "На коловоза",
    statusArriving: "Пристигане",
    statusShunting: "Маневра",
    statusDeparting: "Заминаване",
    statusLoaded: "Натоварен",
    tabPaths: "Коловози",
    tabOcr: "OCR",
    back: "Назад",
    selectStation: "Гара",
    noWagonsOnTrack: "На коловоза няма вагони",
    editWagon: "Редактиране на вагона",
    wagonDeparted: "Вагонът вече е напуснал гарата",
    dataReset: "Данните са нулирани",
    wagonAria: (num, cargo, track) => `Вагон ${num}, ${cargo}, коловоз ${track}`,
    csvTrack: "Коловоз",
    csvNumber: "Номер на вагона",
    csvCargo: "Товар",
    csvOwner: "Принадлежност",
    csvBrutto: "Бруто тегло, т",
    csvNetto: "Нето тегло, т",
    csvDest: "Гара на назначение",
    csvDwell: "Престой, мин",
    csvOcr: "Увереност OCR",
    csvForeign: "Чужд",
    weightRange3070: "30–70 т",
    weightRange7094: "70–94 т",
    sourceLocomotive: "локомотив",
    sourceScale: "везна",
    sourceShunt: "маневра",
    sourceCamera: "камера",
    sourceSystem: "система",
    sourceDispatcher: "диспечер",
    sourceWorker: "работник",
    ocrActionConfirmed: "потвърден",
    ocrActionRejected: "отхвърлен",
    eventArrive: "На {station} пристигна влак — {count} ваг.",
    eventLoad: "На {station} са натоварени с въглища {count} ваг.",
    eventTransfer: "Състав от {from} е изпратен към {to} ({count} ваг.).",
    eventDepart: "Формиран е състав, изпратен: {dest} ({count} ваг.).",
    eventMoveShunt: "Вагон {number}: маневрено преместване на {track}.",
    eventMoveDispatcher: "Диспечерът премести вагон {number} на {track}.",
    eventEdit: "Данните на вагон {number} са променени ({actor}).",
    eventOcrLow: "Камера: номерът е разпознат с ниска увереност — нужна е проверка.",
    eventOcrResolved: "Номер {number} {action} ръчно ({actor}).",
    eventIntake: "{actor} прие състав {trainNo} на {station}, {track} ({count} ваг.).",
    eventSystemStart: "Цифрова гара е стартирана. Симулацията е активна.",
    eventSystemReset: "Данните са нулирани и генерирани наново.",
    tonneUnit: "т",
    consistNote: "Състав {trainNo}",
    introLead:
      "Това е интерактивно демо на диспечерска система за сортировъчна гара. Всички имена на обекти, номера на вагони и тегла в него са измислени.",
    introBulletScreen:
      "Екранът показва гари, коловози и вагони на сортировъчен възел в жива симулация.",
    introBulletRoles:
      "Две роли — диспечерското работно място и мобилното приложение на обходчика — се превключват в страничния панел.",
    introBulletTime:
      "Престоят тече ускорено: 1 реална секунда = 30 симулирани, затова в една сесия вагоните успяват да излязат от зелената зона.",
    introClose: "Към демото",
    demoBadge: "Демо · измислени данни",
    ctaSite: "Направено от BrilliantFlux",
    ctaBooking: "Да обсъдим проект",
    loadingYard: "ЗАРЕЖДАНЕ НА ГАРАТА…",
  },
};

const SOURCE_KEYS = {
  locomotive: "sourceLocomotive",
  scale: "sourceScale",
  shunt: "sourceShunt",
  camera: "sourceCamera",
  system: "sourceSystem",
  dispatcher: "sourceDispatcher",
  worker: "sourceWorker",
};

export function fillTemplate(template, vars) {
  return Object.entries(vars).reduce(
    (s, [k, v]) => s.replace(new RegExp(`\\{${k}\\}`, "g"), () => String(v)),
    template
  );
}

export function formatConsistNote(trainNo, lang) {
  if (!trainNo) return "";
  return fillTemplate((STR[lang] || STR.en).consistNote, { trainNo });
}

export function wagonNoteView(note, consistTrainNo, lang) {
  return {
    note: note ?? "",
    consist: formatConsistNote(consistTrainNo, lang),
  };
}

export function stationName(code, lang) {
  const station = STATIONS.find((s) => s.code === code);
  if (!station) return code;
  return station.name[lang] || station.name.en || code;
}

function actorLabel(actorKey, t) {
  if (actorKey === "dispatcher") return t("actorDispatcher");
  if (actorKey === "worker") return t("actorWorker");
  return t(SOURCE_KEYS[actorKey] || "sourceSystem");
}

export function formatActor(actorKey, t) {
  return actorLabel(actorKey, t);
}

export function trackLabel(idx, t) {
  return `${t("track")} ${idx + 1}`;
}

export function formatEventMessage(event, t, lang) {
  const p = event.params || {};
  const track = trackLabel(p.trackIdx ?? 0, t);

  switch (event.type) {
    case "arrive":
      return fillTemplate(t("eventArrive"), {
        station: stationName(p.stationCode, lang),
        count: p.count,
      });
    case "load":
      return fillTemplate(t("eventLoad"), {
        station: stationName(p.stationCode, lang),
        count: p.count,
      });
    case "transfer":
      return fillTemplate(t("eventTransfer"), {
        from: stationName(p.fromStationCode, lang),
        to: stationName(p.toStationCode, lang),
        count: p.count,
      });
    case "depart":
      return fillTemplate(t("eventDepart"), {
        dest: destLabel(p.destination, lang),
        count: p.count,
      });
    case "move":
      return p.actorKey === "dispatcher"
        ? fillTemplate(t("eventMoveDispatcher"), { number: p.wagonNumber, track })
        : fillTemplate(t("eventMoveShunt"), { number: p.wagonNumber, track });
    case "edit":
      return fillTemplate(t("eventEdit"), {
        number: p.wagonNumber,
        actor: actorLabel(p.actorKey, t),
      });
    case "ocr":
      if (p.variant === "resolved") {
        const action =
          p.action === "confirm" ? t("ocrActionConfirmed") : t("ocrActionRejected");
        return fillTemplate(t("eventOcrResolved"), {
          number: p.number,
          action,
          actor: actorLabel(p.actorKey, t),
        });
      }
      return t("eventOcrLow");
    case "intake":
      return fillTemplate(t("eventIntake"), {
        actor: actorLabel(p.actorKey, t),
        trainNo: p.trainNo,
        station: stationName(p.stationCode, lang),
        track,
        count: p.count,
      });
    case "system":
      return p.variant === "reset" ? t("eventSystemReset") : t("eventSystemStart");
    default:
      return event.type;
  }
}

export function formatEventSource(sourceKey, t) {
  return t(SOURCE_KEYS[sourceKey] || "sourceSystem");
}

export function statusLabel(status, t) {
  const map = {
    parked: t("statusParked"),
    arriving: t("statusArriving"),
    shunting: t("statusShunting"),
    departing: t("statusDeparting"),
    loaded: t("statusLoaded"),
  };
  return map[status] || status;
}

export function cargoLabel(cargo, t) {
  const map = { coal: t("coal"), empty: t("empty"), foreign: t("foreign") };
  return map[cargo] || cargo;
}

export function useT(lang) {
  return useMemo(() => {
    const strings = STR[lang] || STR.en;
    return (key) => strings[key] ?? STR.en[key] ?? key;
  }, [lang]);
}
